import { XIcon } from "lucide-react";
import { AgentAvatar } from "./AgentAvatar.js";
import { splitTextWithMentions } from "./agent-mentions.js";
import { cn } from "./cn.js";
import type { AgentRoom } from "./nav.js";

type ChipProps = {
	agent: AgentRoom;
	/** Removable chip in the composer. */
	onRemove?: () => void;
	className?: string;
};

/** Compact avatar + name pill for @mentions. */
export function MentionChip({ agent, onRemove, className }: ChipProps) {
	return (
		<span className={cn("mc-mention-chip", className)}>
			<AgentAvatar agent={agent} size="sm" />
			<span className="mc-mention-chip-name">{agent.name}</span>
			{onRemove ? (
				<button
					type="button"
					className="mc-mention-chip-remove"
					aria-label={`Remove ${agent.name}`}
					onClick={onRemove}
				>
					<XIcon className="size-3" />
				</button>
			) : null}
		</span>
	);
}

type TextProps = {
	text: string;
	className?: string;
};

/** Render message text with inline mention chips instead of raw @Name. */
export function MentionedText({ text, className }: TextProps) {
	const segments = splitTextWithMentions(text);
	const onlyText =
		segments.length === 1 && segments[0]?.kind === "text"
			? segments[0].value
			: null;

	if (onlyText !== null) {
		return (
			<p className={cn("m-0 whitespace-pre-wrap", className)}>{onlyText}</p>
		);
	}

	return (
		<p className={cn("mc-mentioned-text", className)}>
			{segments.map((segment, index) => {
				if (segment.kind === "text") {
					return (
						<span key={`t-${index}`} className="whitespace-pre-wrap">
							{segment.value}
						</span>
					);
				}
				return (
					<MentionChip
						key={`m-${segment.agent.id}-${index}`}
						agent={segment.agent}
						className="mc-mention-chip--inline"
					/>
				);
			})}
		</p>
	);
}
